import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import {
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Input,
  Row,
  Skeleton,
  Tag,
  Tooltip,
  Typography,
  theme,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGlobe,
  faLayerGroup,
  faDumbbell,
  faPlus,
  faLinkSlash,
  faEye,
  faMagnifyingGlass,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../contexts/auth";
import { useToasts } from "../../contexts/toasts";
import { Roles } from "../../enums/Roles";
import { Programmazione } from "../../types/Programmazione";
import { Programmazioni } from "../../api/Programmazioni";
import { PagedResults } from "../../types/generics";

const { Title, Text } = Typography;

export default function ProgrammazioniPubblicheList() {
  const navigate = useNavigate();
  const { loggedUser } = useAuth();
  const { showToast } = useToasts();
  const { token } = theme.useToken();

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<Programmazione[]>([]);
  const [assignedIds, setAssignedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const isAthlete = loggedUser?.role === Roles.Athlete;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Programmazioni.getPublicCatalog();
      const data = res.data as PagedResults<Programmazione> | { list: Programmazione[] };
      const list = "items" in data ? data.items : data.list;
      setTemplates(list ?? []);

      if (isAthlete) {
        try {
          const assigned = await Programmazioni.getAssignedTemplateIds();
          setAssignedIds(new Set(assigned.data.templateIds ?? []));
        } catch (_) {
          setAssignedIds(new Set());
        }
      }
    } catch (_) {
      showToast("public-error", "Errore nel caricamento delle programmazioni pubbliche", "", "danger");
    } finally {
      setLoading(false);
    }
  }, [isAthlete, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const assign = useCallback(
    async (t: Programmazione) => {
      if (!t.id) return;
      setBusyId(t.id);
      try {
        await Programmazioni.assignTemplate(t.id);
        setAssignedIds((prev) => new Set(prev).add(t.id!));
        showToast("assign-ok", `"${t.name}" assegnata`, "La trovi nelle tue Programmazioni", "success");
      } catch (_) {
        showToast("assign-err", "Errore durante l'assegnazione", "", "danger");
      } finally {
        setBusyId(null);
      }
    },
    [showToast]
  );

  const dissociate = useCallback(
    async (t: Programmazione) => {
      if (!t.id) return;
      setBusyId(t.id);
      try {
        await Programmazioni.dissociateTemplate(t.id);
        setAssignedIds((prev) => {
          const next = new Set(prev);
          next.delete(t.id!);
          return next;
        });
        showToast("dissoc-ok", `"${t.name}" dissociata`, "", "success");
      } catch (_) {
        showToast("dissoc-err", "Errore durante la dissociazione", "", "danger");
      } finally {
        setBusyId(null);
      }
    },
    [showToast]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => t.name?.toLowerCase().includes(q));
  }, [templates, search]);

  const canManage = (t: Programmazione) =>
    loggedUser?.role === Roles.Superadmin ||
    (loggedUser?.role === Roles.Trainer && t.parentId === loggedUser.id);

  return (
    <Container fluid="lg" className="my-3">
      <Helmet>
        <title>Programmazioni Pubbliche</title>
      </Helmet>

      <Flex justify="space-between" align="center" wrap="wrap" gap={12} className="mb-2">
        <div>
          <Title level={2} className="mb-0">
            <FontAwesomeIcon icon={faGlobe} className="me-2" style={{ color: token.colorPrimary }} />
            Programmazioni Pubbliche
          </Title>
          <Text type="secondary">
            Template condivisi da trainer e admin. {isAthlete ? "Assegnali a te stesso per iniziare." : ""}
          </Text>
        </div>
        <Input
          allowClear
          prefix={<FontAwesomeIcon icon={faMagnifyingGlass} className="text-muted" />}
          placeholder="Cerca per nome…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />
      </Flex>

      {loading ? (
        <Row gutter={[16, 16]} className="mt-3">
          {[0, 1, 2, 3].map((i) => (
            <Col xs={24} sm={12} lg={8} key={i}>
              <Card>
                <Skeleton active paragraph={{ rows: 3 }} />
              </Card>
            </Col>
          ))}
        </Row>
      ) : filtered.length === 0 ? (
        <Empty className="mt-5" description="Nessuna programmazione pubblica disponibile" />
      ) : (
        <Row gutter={[16, 16]} className="mt-3">
          {filtered.map((t) => {
            const exercises = t.splits?.reduce((acc, s) => acc + (s.exercises?.length || 0), 0) ?? 0;
            const assigned = t.id ? assignedIds.has(t.id) : false;
            return (
              <Col xs={24} sm={12} lg={8} key={t.id}>
                <Card
                  hoverable
                  className="h-100 cm-public-card"
                  styles={{ body: { display: "flex", flexDirection: "column", height: "100%" } }}
                >
                  <Flex justify="space-between" align="flex-start" gap={8}>
                    <Title level={4} className="mb-1" style={{ flex: 1 }}>
                      {t.name}
                    </Title>
                    <span className="cm-tag cm-tag--brand">
                      <FontAwesomeIcon icon={faGlobe} />
                      Template
                    </span>
                  </Flex>

                  {assigned && (
                    <Tag color="success" icon={<FontAwesomeIcon icon={faCircleCheck} />} className="mb-2 align-self-start">
                      Già assegnata
                    </Tag>
                  )}

                  <Flex gap={20} className="my-3" wrap="wrap">
                    <Flex align="center" gap={6}>
                      <FontAwesomeIcon icon={faLayerGroup} style={{ color: token.colorPrimary }} />
                      <Text>{t.nSplits} Split</Text>
                    </Flex>
                    <Flex align="center" gap={6}>
                      <FontAwesomeIcon icon={faDumbbell} style={{ color: token.colorPrimary }} />
                      <Text>{exercises} Esercizi</Text>
                    </Flex>
                  </Flex>

                  <Flex gap={8} className="mt-auto" wrap="wrap">
                    <Button
                      className="cm-btn cm-btn--ghost"
                      icon={<FontAwesomeIcon icon={faEye} />}
                      onClick={() => navigate(`/programmazioni/${t.id}`)}
                    >
                      Anteprima
                    </Button>

                    {isAthlete &&
                      (assigned ? (
                        <Tooltip title="Rimuovi la tua copia">
                          <Button
                            danger
                            className="cm-btn"
                            loading={busyId === t.id}
                            icon={<FontAwesomeIcon icon={faLinkSlash} />}
                            onClick={() => dissociate(t)}
                          >
                            Dissocia
                          </Button>
                        </Tooltip>
                      ) : (
                        <Button
                          className="cm-btn cm-btn--primary"
                          loading={busyId === t.id}
                          icon={<FontAwesomeIcon icon={faPlus} />}
                          onClick={() => assign(t)}
                        >
                          Assegna a me
                        </Button>
                      ))}

                    {canManage(t) && (
                      <Button
                        type="text"
                        className="cm-btn"
                        onClick={() => navigate(`/programmazioni/${t.id}/edit`)}
                      >
                        Modifica
                      </Button>
                    )}
                  </Flex>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </Container>
  );
}
